import React, { Suspense, useEffect, useState } from 'react';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { NavLink, Outlet, useNavigate, useMatches } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useAcademy } from '../../hooks/useAcademy';
import { useConversations } from '../../hooks/useConversations';
import { useMyPlayer } from '../../hooks/useGroups';
import { SelectedProgramProvider } from '../../contexts/SelectedProgramContext';
import AcademyPortalDataProvider from '../academy/AcademyPortalDataProvider';
import { signOut } from '../../firebase/auth';
import { callFunction } from '../../firebase/functions';
import { installResumeReconnect } from '../../firebase/firestore';
import PwaInstallPrompt from '../academy/PwaInstallPrompt';
import InviteDropdown from '../academy/InviteDropdown';
import ProgramSwitcher from '../academy/ProgramSwitcher';
import PageLoadingFallback from '../shared/PageLoadingFallback';
import LanguageChip from '../shared/LanguageChip';
import AcademyThemeVars from '../academy/AcademyThemeVars';
import { Avatar, Sheet, Tag } from '../academy/ui';
import '../../styles/academyPortal.css';
import {
  HomeIcon,
  CalendarDaysIcon,
  NewspaperIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  DocumentTextIcon,
  DocumentChartBarIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  PencilSquareIcon,
  Squares2X2Icon,
  ArrowRightOnRectangleIcon,
  SparklesIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeSolid,
  CalendarDaysIcon as CalendarSolid,
  NewspaperIcon as NewspaperSolid,
  ChatBubbleLeftRightIcon as ChatSolid,
  UserGroupIcon as UserGroupSolid,
  DocumentTextIcon as DocumentTextSolid,
  DocumentChartBarIcon as DocumentChartBarSolid,
} from '@heroicons/react/24/solid';

const TABS = [
  { to: '/academy/home',      labelKey: 'common:routeTitles.home',      icon: HomeIcon,               solid: HomeSolid },
  { to: '/academy/calendar',  labelKey: 'common:routeTitles.calendar',  icon: CalendarDaysIcon,       solid: CalendarSolid },
  { to: '/academy/feed',      labelKey: 'common:routeTitles.feed',      icon: NewspaperIcon,          solid: NewspaperSolid },
  { to: '/academy/messages',  labelKey: 'common:routeTitles.messages',  icon: ChatBubbleLeftRightIcon, solid: ChatSolid },
  { to: '/academy/groups',    labelKey: 'common:routeTitles.groups',    icon: UserGroupIcon,          solid: UserGroupSolid },
  { to: '/academy/documents', labelKey: 'common:routeTitles.documents', icon: DocumentTextIcon,       solid: DocumentTextSolid },
  { to: '/academy/reports',   labelKey: 'common:routeTitles.reports',   icon: DocumentChartBarIcon,   solid: DocumentChartBarSolid },
];

function Crest({ academy, s = 34, r = 9 }) {
  if (academy?.branding?.logoUrl) {
    return (
      <img
        src={academy.branding.logoUrl}
        alt={academy?.name}
        style={{ width: s, height: s, borderRadius: r, objectFit: 'contain', background: '#fff', flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: s, height: s, borderRadius: r, background: 'var(--accent)', color: '#fff', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: s * 0.44,
      fontWeight: 800, letterSpacing: '-0.03em',
    }}>{academy?.name?.[0]?.toUpperCase() || 'A'}</div>
  );
}

function AccountSheet({ open, onClose, academy }) {
  const { t } = useTranslation();
  const { userProfile, access, isGuardian, isAcademyAdmin, isCoach, isHeadCoach, canManageAcademyPortal } = useAuth();
  const { isPrimaryGuardian } = useMyPlayer();
  const navigate = useNavigate();
  const canSwitchPortal = isGuardian && Array.isArray(access) && access.length > 1;

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const go = (path) => { onClose(); navigate(path); };

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 0',
    borderTop: '1px solid var(--border)', textAlign: 'left', color: 'var(--t1)',
  };

  return (
    <Sheet open={open} onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0 16px' }}>
        <Avatar name={userProfile?.displayName || userProfile?.email || '?'} s={44} tone="accent" />
        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)' }}>{userProfile?.displayName || userProfile?.email}</p>
          <p style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>{academy?.name}</p>
          {isGuardian && (
            <Tag tone={isPrimaryGuardian ? 'accent' : 'neutral'} style={{ marginTop: 6 }}>
              {isPrimaryGuardian
                ? t('academy:portalShell.playerAccountTag', 'Player account')
                : t('academy:portalShell.parentAccountTag', 'Parent/guardian account')}
            </Tag>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 0', borderTop: '1px solid var(--border)' }}>
        <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--t1)' }}>{t('common:language.label')}</span>
        <LanguageChip variant="css-vars" />
      </div>
      <button type="button" onClick={() => go('/academy/profile')} className="ap-tap" style={rowStyle}>
        <PencilSquareIcon style={{ width: 17, height: 17, color: 'var(--t3)' }} />
        <span style={{ fontSize: 13.5, fontWeight: 500, flex: 1 }}>{t('common:actions.profile')}</span>
        <ChevronRightIcon style={{ width: 14, height: 14, color: 'var(--t3)' }} />
      </button>
      {canSwitchPortal && (
        <button type="button" onClick={() => go('/portal/choose')} className="ap-tap" style={rowStyle}>
          <Squares2X2Icon style={{ width: 17, height: 17, color: 'var(--t3)' }} />
          <span style={{ fontSize: 13.5, fontWeight: 500, flex: 1 }}>{t('common:actions.switchPortal')}</span>
          <ChevronRightIcon style={{ width: 14, height: 14, color: 'var(--t3)' }} />
        </button>
      )}
      {canManageAcademyPortal && (
        <button type="button" onClick={() => go('/academy/programs')} className="ap-tap" style={rowStyle}>
          <UserGroupIcon style={{ width: 17, height: 17, color: 'var(--t3)' }} />
          <span style={{ fontSize: 13.5, fontWeight: 500, flex: 1 }}>{t('academy:portalShell.managePrograms', 'Manage programs')}</span>
          <ChevronRightIcon style={{ width: 14, height: 14, color: 'var(--t3)' }} />
        </button>
      )}
      {isHeadCoach && (
        <button type="button" onClick={() => go('/academy/club')} className="ap-tap" style={rowStyle}>
          <Cog6ToothIcon style={{ width: 17, height: 17, color: 'var(--t3)' }} />
          <span style={{ fontSize: 13.5, fontWeight: 500, flex: 1 }}>{t('academy:clubSettings.navLabel', 'Club settings')}</span>
          <ChevronRightIcon style={{ width: 14, height: 14, color: 'var(--t3)' }} />
        </button>
      )}
      {isAcademyAdmin && (access.includes('onboarding') || access.includes('crm')) && (
        <button type="button" onClick={() => go('/')} className="ap-tap" style={rowStyle}>
          <Squares2X2Icon style={{ width: 17, height: 17, color: 'var(--t3)' }} />
          <span style={{ fontSize: 13.5, fontWeight: 500, flex: 1 }}>{t('common:actions.backToAdmin')}</span>
          <ChevronRightIcon style={{ width: 14, height: 14, color: 'var(--t3)' }} />
        </button>
      )}
      {(isCoach || (isGuardian && !isPrimaryGuardian)) && (
        <button type="button" onClick={() => go('/academy/assistant')} className="ap-tap" style={rowStyle}>
          <SparklesIcon style={{ width: 17, height: 17, color: 'var(--t3)' }} />
          <span style={{ fontSize: 13.5, fontWeight: 500, flex: 1 }}>
            {isGuardian ? t('academy:guardianAssistant.navLabel', 'Ask about your player') : t('common:routeTitles.assistant')}
          </span>
          <ChevronRightIcon style={{ width: 14, height: 14, color: 'var(--t3)' }} />
        </button>
      )}
      {isCoach && access.includes('onboarding') && (
        <button type="button" onClick={() => go('/admin/calendar')} className="ap-tap" style={rowStyle}>
          <CalendarDaysIcon style={{ width: 17, height: 17, color: 'var(--t3)' }} />
          <span style={{ fontSize: 13.5, fontWeight: 500, flex: 1 }}>{t('common:actions.goToOnboarding')}</span>
          <ChevronRightIcon style={{ width: 14, height: 14, color: 'var(--t3)' }} />
        </button>
      )}
      <button type="button" onClick={handleSignOut} className="ap-tap" style={{ ...rowStyle, color: '#b91c1c' }}>
        <ArrowRightOnRectangleIcon style={{ width: 17, height: 17 }} />
        <span style={{ fontSize: 13.5, fontWeight: 600 }}>{t('common:actions.signOut')}</span>
      </button>
      <div style={{ height: 10 }} />
    </Sheet>
  );
}

export default function AcademyPortalShell() {
  useEffect(() => installResumeReconnect(), []);
  return (
    <SelectedProgramProvider>
      <AcademyPortalDataProvider>
        <ShellChrome />
      </AcademyPortalDataProvider>
    </SelectedProgramProvider>
  );
}

function ShellChrome() {
  const { t } = useTranslation();
  const { user, userProfile, canManageAcademyPortal, isGuardian } = useAuth();

  // Self-heal: make sure a player/parent is in every group (and group chat)
  // their child is in — see syncMyGroupMemberships in functions/index.js.
  // Once per sign-in; silent and non-blocking.
  useEffect(() => {
    if (!isGuardian || !user?.uid) return;
    callFunction('syncMyGroupMemberships')().catch(() => { /* non-blocking */ });
  }, [isGuardian, user?.uid]);
  const { academy } = useAcademy();
  const { unreadCount } = useConversations();
  const matches = useMatches();
  const routeTitle = matches.at(-1)?.handle?.title ?? '';
  const [accountOpen, setAccountOpen] = useState(false);
  // Only ONE chrome (mobile or desktop) is ever mounted — both used to
  // render with one hidden via CSS, which mounted every page (and every
  // Firestore listener/effect inside it) twice.
  const isDesktop = useIsDesktop();

  return (
    <AcademyThemeVars academy={academy}>
      {/* Mobile chrome (< 1024px): navy header, scrolling content, navy tab bar */}
      {!isDesktop && (
        <div className="flex flex-col min-h-screen">
          <header style={{ background: 'var(--navy)', paddingTop: 'max(env(safe-area-inset-top), 14px)', flexShrink: 0 }} className="sticky top-0 z-40">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 14px 12px' }}>
              <Crest academy={academy} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {academy?.name || t('common:routeTitles.academy')}
                </p>
                {canManageAcademyPortal
                  ? <ProgramSwitcher variant="subline" fallback={<span style={{ fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,.52)' }}>{t('academy:portalShell.academyPortal', 'Academy Portal')}</span>} />
                  : <span style={{ fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,.52)' }}>{t('academy:portalShell.academyPortal', 'Academy Portal')}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
                {canManageAcademyPortal && <InviteDropdown variant="icon" />}
                <button type="button" onClick={() => setAccountOpen(true)} className="ap-tap" style={{ borderRadius: '50%' }}>
                  <Avatar name={userProfile?.displayName || userProfile?.email || '?'} s={32} tone="navy" />
                </button>
              </div>
            </div>
          </header>

          <main className="ap-hide-sb flex-1" style={{ padding: '16px 14px 26px' }}>
            <Suspense fallback={<PageLoadingFallback />}>
              <Outlet />
            </Suspense>
          </main>

          <PwaInstallPrompt academy={academy} />

          <nav data-ap-tabbar style={{ background: 'var(--navy)', paddingBottom: 'max(env(safe-area-inset-bottom), 12px)', flexShrink: 0, borderTop: '1px solid rgba(255,255,255,.07)' }} className="sticky bottom-0 z-40">
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${TABS.length},minmax(0,1fr))` }}>
              {TABS.map(({ to, labelKey, icon: Icon, solid: Solid }) => {
                const badge = to === '/academy/messages' && unreadCount > 0 ? unreadCount : null;
                return (
                  <NavLink key={to} to={to} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '9px 0 7px', position: 'relative' }}>
                    {({ isActive }) => (
                      <>
                        {isActive && <span style={{ position: 'absolute', top: 0, width: 26, height: 2.5, borderRadius: 3, background: 'var(--accent)' }} />}
                        <span style={{ position: 'relative' }}>
                          {isActive ? <Solid style={{ width: 20, height: 20, color: 'var(--accent)' }} /> : <Icon style={{ width: 20, height: 20, color: 'rgba(255,255,255,.42)' }} strokeWidth={1.7} />}
                          {badge && (
                            <span style={{
                              position: 'absolute', top: -3, right: -7, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 7,
                              background: 'var(--accent)', color: '#fff', fontSize: 9, fontWeight: 800,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px var(--navy)',
                            }}>{badge}</span>
                        )}
                      </span>
                      <span style={{ fontSize: 10.5, fontWeight: isActive ? 700 : 500, letterSpacing: '-0.01em', color: isActive ? '#fff' : 'rgba(255,255,255,.42)', maxWidth: '100%', padding: '0 2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t(labelKey)}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </div>
      )}

      {/* Desktop chrome ( >=1024px): navy sidebar, topbar, single scrolling column */}
      {isDesktop && (
        <div className="flex" style={{ height: '100vh' }}>
          <aside style={{ width: 236, flexShrink: 0, background: 'var(--navy)', display: 'flex', flexDirection: 'column', padding: '18px 14px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px 16px' }}>
              <Crest academy={academy} s={32} r={8} />
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {academy?.name || t('common:routeTitles.academy')}
                </p>
                <p style={{ fontSize: 10.5, color: 'rgba(255,255,255,.4)', marginTop: 1 }}>{t('academy:portalShell.academyPortal', 'Academy Portal')}</p>
              </div>
            </div>
            {canManageAcademyPortal && (
              <div style={{ marginBottom: 16 }}>
                <ProgramSwitcher variant="boxed" />
              </div>
          )}
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {TABS.map(({ to, labelKey, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8, position: 'relative', textAlign: 'left',
                  background: isActive ? 'rgba(255,255,255,.1)' : 'transparent',
                })}
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span style={{ position: 'absolute', left: -9, top: '50%', marginTop: -9, width: 3, height: 18, borderRadius: 2, background: 'var(--accent)' }} />}
                    <Icon style={{ width: 17, height: 17, color: isActive ? 'var(--accent)' : 'rgba(255,255,255,.52)' }} strokeWidth={isActive ? 2 : 1.7} />
                    <span style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, letterSpacing: '-0.01em', flex: 1, color: isActive ? '#fff' : 'rgba(255,255,255,.52)' }}>{t(labelKey)}</span>
                    {to === '/academy/messages' && unreadCount > 0 && (
                      <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: 'var(--accent)', color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unreadCount}</span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <div style={{ marginTop: 'auto' }}>
            {canManageAcademyPortal && <InviteDropdown variant="button" />}
            <button type="button" onClick={() => setAccountOpen(true)} className="ap-tap" style={{
              display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 8px', borderRadius: 8, textAlign: 'left',
              borderTop: '1px solid rgba(255,255,255,.07)',
            }}>
              <Avatar name={userProfile?.displayName || userProfile?.email || '?'} s={28} tone="solid" />
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userProfile?.displayName || userProfile?.email}</p>
              </div>
              <ChevronUpIcon style={{ width: 13, height: 13, color: 'rgba(255,255,255,.4)' }} />
            </button>
          </div>
        </aside>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <header style={{ height: 58, flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, padding: '0 22px' }}>
            <span style={{ fontSize: 12.5, color: 'var(--t3)' }}>{academy?.name}</span>
            <ChevronRightIcon style={{ width: 13, height: 13, color: 'var(--t3)' }} />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--t1)' }}>{routeTitle ? t(routeTitle) : ''}</span>
            <div style={{ marginLeft: 'auto' }}>
              <LanguageChip variant="css-vars" />
            </div>
          </header>
          <main className="ap-hide-sb" style={{ flex: 1, overflowY: 'auto' }}>
            <div style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 26px 40px' }}>
              <Suspense fallback={<PageLoadingFallback />}>
                <Outlet />
              </Suspense>
            </div>
          </main>
        </div>
      </div>
      )}

      <AccountSheet open={accountOpen} onClose={() => setAccountOpen(false)} academy={academy} />
    </AcademyThemeVars>
  );
}
